import { DynamicBlock } from "@components/block";
import { SingleHeader } from "@components/header";
import ShareSocialMediaButtons from "@components/share-social-media-buttons";
import { SimplePagination } from "@components/simple-pagination";
import { useWindowSize } from "@components/window-size-provider";
import useScrollToLocation from "@hooks/useScrollToLocation";
import { ShowElements } from "@lib/constants";
import { sanitizeSearchInput } from "@lib/utils";
import { getWordpressVars } from "@lib/wordpressVars";
import PostAuthors from "@mwPartials/post/PostAuthors";
import RelatedPosts from "@mwPartials/post/RelatedPosts";
import { useGetPageByIdQuery } from "@services/Page";
import { Col, Layout, Row, Spin } from "antd";
import { useLocation } from "react-router-dom";
import { useGetHeaderAdsQuery } from "@mwServices/Ads";
import { useEffect, useState } from "react";
import { HeaderAd } from "@components/header-ad";

interface ArticleProps {
    objectId?: number;
}

const defaultProps: ArticleProps = {};

const Article = () => {
    const location = useLocation();
    const queryParams = new URLSearchParams(sanitizeSearchInput(location.search));
    const search = queryParams.get('search');
    const ID = getWordpressVars().ID;
    const ads = getWordpressVars().ads;
    const { isLargeScreen } = useWindowSize();

    const headerAdsQuery = useGetHeaderAdsQuery({ID});
    const [desktopHeaderAd, setDesktopHeaderAd] = useState<any>();
    const [mobileHeaderAd, setMobileHeaderAd] = useState<any>();
    useEffect(() => {
        if (headerAdsQuery.data) {
            headerAdsQuery.data.forEach((ad) => {
                if (ad.isMobile) {
                    setMobileHeaderAd(ad);
                } else {
                    setDesktopHeaderAd(ad);
                }
            });
        }
    }, [headerAdsQuery.data]);

    const {
        isLoading,
        data,
    } = useGetPageByIdQuery({
        id: ID, parts: [
            'ID', 'title', 'showElements',
            'link', 'date', 'image', 'blocks', 'ads'
        ]
    });

    useScrollToLocation();

    return (
        <Layout className="page-article">
            <Row className={`mar-bot-30`}>
                <Col span={24}>
                </Col>
            </Row>
            <Row justify={`center`} className={`mar-bot-30`}>
                <Col lg={22} xs={24}>
                    <Row
                        className={isLargeScreen ? 'grid-auto-350' : ''}
                        justify={`center`}
                        gutter={[30, 30]}
                    >
                        <Col className={`border-r-1-light-grey`}>
                            <Spin size="large" spinning={isLoading}>
                                {data?.results?.showElements?.includes(ShowElements.PAGINATION_TOP)
                                    && <Row justify={"center"}>
                                        <Col lg={18} xs={23}>
                                            {data?.results?.simplePagination && (
                                                <SimplePagination
                                                    className={`mar-bot-30`}
                                                    next={data.simplePagination.next}
                                                    previous={data.simplePagination.previous}
                                                />
                                            )}
                                        </Col>
                                    </Row>
                                }
                                {data?.results?.showElements?.includes(ShowElements.HERO_HEADER)
                                    && <SingleHeader
                                        className={`hero-subheader mar-bot-30`}
                                        title={null}
                                        imgSrc={`${data?.results?.image.src}`}
                                        parallax={false}
                                    />}
                                <Row className="content" justify={"center"}>
                                    {(isLargeScreen && desktopHeaderAd) &&
                                        <HeaderAd
                                            campaignUrl={desktopHeaderAd.campaignUrl}
                                            adId={desktopHeaderAd.adId}
                                            imageUrl={desktopHeaderAd.imageUrl}
                                            style={{marginBottom: '24px'}}
                                        />
                                    }
                                    {(!isLargeScreen && mobileHeaderAd) &&
                                        <HeaderAd
                                            campaignUrl={mobileHeaderAd.campaignUrl}
                                            adId={mobileHeaderAd.adId}
                                            imageUrl={mobileHeaderAd.imageUrl}
                                            style={{marginBottom: '20px'}}
                                        />
                                    }
                                    <Col lg={18} xs={23}>
                                        <h1 className="article-header">{data?.results?.title}</h1>
                                        {data?.results?.showElements?.includes(ShowElements.DATE)
                                            && <p className="article-lead">{data?.results?.date}</p>}
                                    </Col>
                                    {data?.results?.showElements?.includes(ShowElements.SHARE_BUTTONS)
                                        && <Col lg={18} xs={23}>
                                            {data?.results?.socialShare
                                                && <ShareSocialMediaButtons
                                                    {...data?.results?.socialShare}
                                                />}
                                        </Col>
                                    }
                                    <Col lg={18} xs={23}>
                                        <DynamicBlock
                                            key={data?.results?.id}
                                            blocks={data?.results?.blocks}
                                            search={search}
                                        />
                                    </Col>
                                </Row>
                                {data?.results?.showElements?.includes(ShowElements.RELATED_POSTS)
                                    && <Row justify={"center"}>
                                        <Col lg={18} xs={23}>
                                            {data && (
                                                <>
                                                    <hr className="thin" />
                                                    <RelatedPosts attrs={{
                                                        tags: data?.results?.taxonomy?.tags || [],
                                                        category: data?.results?.taxonomy?.category || [],
                                                        taxonomies: ['post_tag', 'category'],
                                                        page: 1,
                                                        perPage: 2,
                                                    }} />
                                                </>
                                            )}
                                        </Col>
                                    </Row>}
                                {data?.results?.showElements?.includes(ShowElements.AUTHORS)
                                    && <Row justify={"center"}>
                                        <Col lg={18} xs={23}>
                                            {data?.results?.authors && (
                                                <PostAuthors className="border-top-thick" data={data?.results?.authors} />
                                            )}
                                        </Col>
                                    </Row>
                                }
                                {data?.results?.showElements?.includes(ShowElements.PAGINATION_BOTTOM)
                                    && <Row justify={"center"}>
                                        <Col lg={18} xs={23}>
                                            {data?.results?.simplePagination && (
                                                <SimplePagination
                                                    justify={`end`}
                                                    className={`mar-top-30`}
                                                    next={data.simplePagination.next}
                                                    previous={data.simplePagination.previous}
                                                />
                                            )}
                                        </Col>
                                    </Row>
                                }
                            </Spin>
                        </Col>
                        <Col>
                            {ads && <DynamicBlock blocks={ads} />}
                        </Col>
                    </Row>
                </Col>
            </Row>
        </Layout>
    );
};

Article.defaultProps = defaultProps;

export default Article;
